"use client";

import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { useActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import {
  dedupeCardIcons,
  extractCardIcons,
  extractChainIdsFromDefinition,
  extractConnectorsFromDefinition,
  partitionCardIcons,
  prioritizeCardIcons,
  type CardIcon,
  type CardIconSource,
} from "@/lib/workflow/cardIcons";
import { useConnectorsStore, type ConnectorType } from "@/stores/connectorsStore";
import { useMemo } from "react";

export interface UseWorkflowCardIconsResult {
  cardIcons: CardIcon[];
  triggers: ReturnType<typeof useTriggers>["triggers"];
  actions: ReturnType<typeof useActions>["actions"];
  connectorTypes: ConnectorType[];
  chainInfoMap: ReturnType<typeof useChainInfoMap>["data"];
}

/**
 * Derives the deduplicated CardIcon[] used by workflow + template cards.
 * Resolves triggers/actions/connectorTypes so a single consumer can render
 * the tiles without re-fetching catalog data.
 */
export function useWorkflowCardIcons(source: CardIconSource): UseWorkflowCardIconsResult {
  const { triggers } = useTriggers();
  const { actions } = useActions();
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);

  const enrichedSource = useMemo<CardIconSource>(() => {
    if (source.connectors && source.chainIds) return source;
    return {
      ...source,
      connectors: source.connectors ?? extractConnectorsFromDefinition(source.definition),
      chainIds: source.chainIds ?? extractChainIdsFromDefinition(source.definition),
    };
  }, [source]);

  const cardIcons = useMemo(() => {
    const raw = extractCardIcons(enrichedSource);
    return dedupeCardIcons(raw, triggers, actions);
  }, [enrichedSource, triggers, actions]);

  const chainIds = useMemo(
    () => cardIcons.filter((i): i is CardIcon & { kind: "chain" } => i.kind === "chain").map(i => i.chainId),
    [cardIcons],
  );
  const { data: chainInfoMap } = useChainInfoMap(chainIds);

  return { cardIcons, triggers, actions, connectorTypes, chainInfoMap };
}

export interface UsePrioritizedCardIconsResult extends UseWorkflowCardIconsResult {
  prioritizedIcons: CardIcon[];
}

/**
 * Convenience adapter for callers that always want the prioritized icon list
 * (brand-backed first, generic last). Saves the duplicated useMemo at every
 * card consumer.
 */
export function usePrioritizedCardIcons(source: CardIconSource): UsePrioritizedCardIconsResult {
  const result = useWorkflowCardIcons(source);
  const prioritizedIcons = useMemo(
    () => prioritizeCardIcons(result.cardIcons, result.triggers, result.actions),
    [result.cardIcons, result.triggers, result.actions],
  );
  return { ...result, prioritizedIcons };
}

export interface UseBrandCardIconsResult extends UseWorkflowCardIconsResult {
  brandIcons: CardIcon[];
}

/**
 * Adapter for callers that only want brand-backed icons (drops generic
 * AI-only nodes). Used by template cards that don't render the placeholder
 * tile when no brand resolves.
 */
export function useBrandCardIcons(source: CardIconSource): UseBrandCardIconsResult {
  const result = useWorkflowCardIcons(source);
  const brandIcons = useMemo(
    () => partitionCardIcons(result.cardIcons, result.triggers, result.actions).brand,
    [result.cardIcons, result.triggers, result.actions],
  );
  return { ...result, brandIcons };
}
